import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getCouponsQuery, addCouponMutation, updateCouponPositionsMutation } from '../../../../queries/queries';
import { LoadingTable, sortList } from '../../../../common/index';
import ModalAddCoupons from './manage/ModalAddCoupons';
import ModalCouponsManage from './manage/ModalCouponsManage';
import { ManageMode } from '../../../../types/types';
import { useZoneContext } from '../../../../context/ZoneContext';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useCompaniesTableContext } from '../../../../context/CompaniesTableContext';
import { useConfirmationModalContext } from '../../../../context/ConfirmationModalContext';
import { calculateCompanyTotalForVisibleBooklets } from '../../utils/couponCalculations';

import { FaLock, FaUnlock } from 'react-icons/fa';
import { SortableRow } from './SortableRow';

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';

import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const arrayMove = (arr, from, to) => {
  const copy = [...arr];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

const normalizePositions = (list) => {
  return list.map((c, index) => ({
    ...c,
    position: index,
  }));
};

function CouponsTable({ zoneId, contactEmails, company, checked, isCompanyInView, companyHeight, companyIndex }) {
  const { filteredBooklets, companiesFilter, setCompaniesFilter, allBooklets } = useZoneContext();
  const confirmationModalContext = useConfirmationModalContext();
  const {
    markCompanyAsLoaded,
    currentBatchStart,
    batchSize,
    totalCompaniesCount,
    updateTotalUsedCoupons,
    isDateActive,
    getDateClassName,
  } = useCompaniesTableContext();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  const [addCoupon, { loading: addCouponLoading }] = useMutation(addCouponMutation);
  const [updatePositions, { loading: updatingPositionsLoading }] = useMutation(updateCouponPositionsMutation,
    {
      onCompleted: () => {
        fetchCoupons(); // refetch coupons
      },
    }
  );
  const [fetchCoupons, { data, loading, error }] = useLazyQuery(getCouponsQuery, {
    variables: { companyId: company._id },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {

      markCompanyAsLoaded(company._id, data.coupons);

      const totalSold = calculateCompanyTotalForVisibleBooklets(data.coupons, filteredBooklets)

      updateTotalUsedCoupons(company._id, totalSold)
    },
  });

  const [sortBy, setSortBy] = useState('position');
  const [sortOrder, setSortOrder] = useState(1);
  const [isHovered, setIsHovered] = useState(false);
  const [isAddCouponsModalOpen, setIsAddCouponsModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [bulkEditingCoupons, setBulkEditingCoupons] = useState(null);
  const [selectedCouponIds, setSelectedCouponIds] = useState({});
  const [isEditingPosition, setIsEditingPostion] = useState(false);
  const [localCoupons, setLocalCoupons] = useState([]);

  const coupons = data?.coupons || [];
  const sort = { sortBy, sortOrder, setSortBy, setSortOrder };

  // Determines if this company should be loaded in the current batch:
  // - companyIndex >= currentBatchStart: Company is not in a previous batch
  // - companyIndex < currentBatchStart + batchSize: Company is within the current batch size
  const isInCurrentBatch = companyIndex >= currentBatchStart && companyIndex < currentBatchStart + batchSize;

  // Fetch coupons when it's this company's turn OR when it comes into view
  const shouldFetchCoupons = totalCompaniesCount && (isCompanyInView || isInCurrentBatch);

  useEffect(() => {
    if (shouldFetchCoupons) {
      fetchCoupons();
    }
  }, [fetchCoupons, shouldFetchCoupons]);

  // Recalculate total when filteredBooklets changes
  useEffect(() => {
    if (data?.coupons && filteredBooklets) {
      const totalSold = calculateCompanyTotalForVisibleBooklets(data.coupons, filteredBooklets);

      updateTotalUsedCoupons(company._id, totalSold);
    }
  }, [filteredBooklets, data?.coupons, company._id, updateTotalUsedCoupons]);

  // Function to determine if a company should be hidden
  const shouldHideCompany = (companyId, selectedBooklet, checkedCoupons, coupons) => {
    if (!selectedBooklet || !checkedCoupons || !coupons) return false;

    const relevantCheckedCoupons = checkedCoupons.filter(checkedItem => selectedBooklet.couponIds.includes(checkedItem.split('-')[1]));
    const couponIdsInChecked = relevantCheckedCoupons.map(checkedItem => checkedItem.split('-')[1]);
    return !coupons.some(coupon => coupon.companyId === companyId && couponIdsInChecked.includes(coupon._id));
  };

  // Get the relevant checked coupons for the selected booklet
  const relevantCheckedCoupons = useMemo(() => {
    return checked.filter(checkedItem => {
      const booklet = filteredBooklets?.find(b => b._id === companiesFilter.selectedBookletId);
      return booklet && booklet.couponIds.includes(checkedItem.split('-')[1]);
    });
  }, [checked, companiesFilter, filteredBooklets]);

  // useEffect hook to update hiddenCompanies
  useEffect(() => {
    if (!company._id || !companiesFilter) return;

    const selectedBooklet = filteredBooklets?.find(b => b._id === companiesFilter.selectedBookletId);

    const hideCompany = shouldHideCompany(company._id, selectedBooklet, relevantCheckedCoupons, coupons);

    setCompaniesFilter(prevState => {
      const hiddenCompaniesSet = new Set(prevState.hiddenCompanies);
      const needsUpdate = hideCompany ? hiddenCompaniesSet.add(company._id).size !== prevState.hiddenCompanies.length
        : hiddenCompaniesSet.delete(company._id);

      return needsUpdate ? { ...prevState, hiddenCompanies: Array.from(hiddenCompaniesSet) } : prevState;
    });
  }, [companiesFilter, relevantCheckedCoupons, coupons, company._id, setCompaniesFilter]);

  useEffect(() => {
    if (data?.coupons) {
      setLocalCoupons(sortList(data.coupons, sort));
    }
  }, [data]);

  const handleDuplicate = (e, coupon) => {
    e.stopPropagation();
    if (addCouponLoading) return;

    addCoupon({
      variables: {
        companyId: company._id,
        imageURL: coupon.imageURL,
        discount: coupon.discount,
        smallTitle: coupon.smallTitle,
        productLink: coupon.productLink,
        terms: coupon.terms,
        description: coupon.description,
        code: coupon.code,
      },
      refetchQueries: [{ query: getCouponsQuery, variables: { companyId: company._id } }]
    }).then((res) => {
      console.log('duplicated', res);
      fetchCoupons();
    }).catch((err) => {
      console.log('error', err);
    });
  }

  const isCouponInActiveBooklet = (couponId) => {

    for (const booklet of allBooklets) {
      if (isDateActive(booklet.buyableFrom, booklet.buyableTo)) {
        if (booklet.couponIds.includes(couponId)) {
          return true;
        }
      }
    }
    return false;
  }

  const handleConfirmEdit = async (coupon) => {
    const isInActiveBooklet = isCouponInActiveBooklet(coupon._id);

    if (!isInActiveBooklet) {
      setEditingCoupon(coupon);
      return;
    }
    const result = await confirmationModalContext.showConfirmation('Kupongen är med i ett aktivt häfte!', 'Är du säker på att du vill redigera kupongen i det aktiva häftet?');

    if (!result) {
      return;
    } else {
      setEditingCoupon(coupon);
    }
  }

  const handleEditCoupon = async (e, coupon) => {
    e.stopPropagation();
    const isInActiveBooklet = isCouponInActiveBooklet(coupon._id);

    if (isInActiveBooklet) {
      const result = await confirmationModalContext.showConfirmation('Kupongen är med i ett aktivt häfte!', 'Är du säker på att du vill redigera kupongen i det aktiva häftet?');
      if (!result) {
        return;
      }
    }

    setEditingCoupon(coupon);
  }

  const handleSelectAllCouponsInCompany = (booklet, companyId) => {
    const ids = coupons
      .filter(c => c.companyId === companyId)
      .map(c => c._id);

    setSelectedCouponIds(prev => ({
      ...prev,
      [booklet._id]: ids
    }));
  };

  const handleSelectAllActiveCouponsInCompany = (booklet, companyId) => {
    const ids = coupons
      .filter(c => c.companyId === companyId)
      .map(c => c._id);

    let selectedCoupons = [];
    for (const id of ids) {
      if (booklet.couponIds.includes(id)) {
        selectedCoupons.push(id);
      }
    }
    setSelectedCouponIds(prev => ({
      ...prev,
      [booklet._id]: selectedCoupons
    }));
  }

  const handleCouponsModalClose = () => {
    setEditingCoupon(null);
    setBulkEditingCoupons(null);
    fetchCoupons();
    setSelectedCouponIds([]);
  };

  const handleOpenBulkEditor = (selectedCoupons) => {
    if (!selectedCoupons || selectedCoupons.length === 0) return;
    setEditingCoupon(null);
    setBulkEditingCoupons(selectedCoupons);
  };

  const handleToggleEditMode = () => {
    setIsEditingPostion(p => !p);
  }

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localCoupons.findIndex(c => c._id === active.id);
    const newIndex = localCoupons.findIndex(c => c._id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(localCoupons, oldIndex, newIndex);
    const normalized = normalizePositions(reordered);

    setLocalCoupons(normalized);

    // update on backend
    updatePositions({
      variables: {
        coupons: normalized.map(c => ({
          _id: c._id,
          position: c.position,
        })),
      },
    }).catch(console.error);
  }

  return (
    <>
      {isAddCouponsModalOpen && (
        <ModalAddCoupons
          isOpen={isAddCouponsModalOpen}
          onClose={() => setIsAddCouponsModalOpen(false)}
          company={company}
          refetchCoupons={fetchCoupons}
        />
      )}
      {editingCoupon && (
        <ModalCouponsManage
          isOpen={!!editingCoupon}
          onClose={handleCouponsModalClose}
          quantity={1}
          company={company}
          mode={ManageMode.EDIT}
          coupons={[editingCoupon]}
          refetchCoupons={fetchCoupons}
        />
      )}
      {bulkEditingCoupons && (
        <ModalCouponsManage
          isOpen={!!bulkEditingCoupons}
          onClose={handleCouponsModalClose}
          quantity={bulkEditingCoupons.length}
          company={company}
          mode={ManageMode.EDIT}
          coupons={bulkEditingCoupons}
          refetchCoupons={fetchCoupons}
        />
      )}

      {coupons.length > 0 && (
        <tr className="locked-section">
          <td></td>
          <td colSpan={3}>
            <button className="locked-content" onClick={handleToggleEditMode}>
              {isEditingPosition ? <FaUnlock size={12} /> : <FaLock size={12} />}
              <span>Ändra ordning</span>
            </button>
          </td>
        </tr>
      )}
      <tr id={`locator-${company._id}`}></tr>
      {
        // Table row for coupon
        !data?.coupons ? <LoadingTable loading={loading} error={error} /> : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localCoupons.map(c => c._id)}
              strategy={verticalListSortingStrategy}
            >
              {localCoupons.map((couponObj, index) => (
                <SortableRow
                  key={couponObj._id}
                  coupon={couponObj}
                  coupons={coupons}
                  index={index}
                  isEditingPosition={isEditingPosition}
                  isUpdating={updatingPositionsLoading}
                  {...{
                    companiesFilter,
                    company,
                    isHovered,
                    setIsHovered,
                    handleConfirmEdit,
                    handleDuplicate,
                    handleEditCoupon,
                    filteredBooklets,
                    selectedCouponIds,
                    setSelectedCouponIds,
                    contactEmails,
                    zoneId,
                    isCompanyInView,
                    companyHeight,
                    companyIndex,
                    fetchCoupons,
                    getDateClassName,
                    isDateActive,
                    handleOpenBulkEditor,
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
        )
      }
      <tr className='add-coupons'>
        <td></td>
        <td colSpan={3} className={`${data?.coupons.length >= 1 ? 'add-coupons-cell-with-coupons' : 'add-coupons-cell'}  ${companiesFilter.hiddenCompanies.includes(company._id) ? 'faded' : ''}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}>
          <button className={`add-coupon ${data?.coupons.length >= 1 ? 'with-coupons-btn' : ''} ${coupons.length === 0 ? 'visable' : isHovered ? 'visable' : ''}`} onClick={() => setIsAddCouponsModalOpen(true)}>
            + Kupong
          </button>
        </td>
        {filteredBooklets?.map((b, i) => {
          return (
            <td key={i} className={`${getDateClassName(isDateActive(b.buyableFrom, b.buyableTo))}`}></td>)
        })}
      </tr>

      {coupons.length > 0 && (
        <tr
          className='select-coupons'
        >
          <td></td>
          <td colSpan={3}></td>
          {filteredBooklets?.map((b, i) => {
            return (
              <td key={i} className={`${getDateClassName(isDateActive(b.buyableFrom, b.buyableTo))}`}>
                <button
                  type="button"
                  onClick={() => handleSelectAllActiveCouponsInCompany(b, company._id)}
                  className="select-all-btn"
                  title="Markera aktiva kuponger i häftet"
                >
                  Välj aktiva
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectAllCouponsInCompany(b, company._id)}
                  className="select-all-btn"
                  title="Markera alla kuponger i häftet"
                >
                  Välj alla
                </button>
              </td>)
          })}
        </tr>
      )}
    </>
  );
}

CouponsTable.propTypes = {
  zoneId: PropTypes.string.isRequired,
  contactEmails: PropTypes.array.isRequired,
  company: PropTypes.object.isRequired,
  checked: PropTypes.array.isRequired,
  isCompanyInView: PropTypes.bool.isRequired,
};

export default CouponsTable;
